'use client'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Icon } from './icon'
import { LIMITE_MEI_ANO } from '@/lib/calculos/constantes'
import { tetoMeiCurto } from '@/lib/calculos/teto-texto'

/**
 * Amostra VIVA do Premium — o cliente vê as telas (Painel, Orçamento,
 * Comprovante) rodando ao vivo, com barras preenchendo e números, em vez de um
 * chip estático. Auto-avança a cada ~4,5s, pausa quando a pessoa interage, e
 * respeita prefers-reduced-motion. Vem SEMPRE depois do botão de exportar —
 * é vitrine, não pedágio.
 */

type SlideKey = 'painel' | 'orcamento' | 'comprovante'

const TABS: { key: SlideKey; label: string; icon: 'wallet' | 'calendar' | 'download' }[] = [
  { key: 'painel', label: 'Painel', icon: 'wallet' },
  { key: 'orcamento', label: 'Orçamento', icon: 'calendar' },
  { key: 'comprovante', label: 'Comprovante', icon: 'download' },
]

const AUTO_MS = 4500

// Exemplo do painel: faturamento do ano contra o teto do ano corrente.
const FAT_EXEMPLO = 48600
const PCT_TETO_EXEMPLO = Math.round((FAT_EXEMPLO / LIMITE_MEI_ANO) * 100)

export function PremiumShowcase() {
  const reduce = useReducedMotion()
  const [i, setI] = useState(0)
  const [paused, setPaused] = useState(false)
  const active = TABS[i].key

  // auto-avança; pausa em hover/foco/toque e quando reduced-motion
  useEffect(() => {
    if (paused || reduce) return
    const t = window.setInterval(() => setI((v) => (v + 1) % TABS.length), AUTO_MS)
    return () => window.clearInterval(t)
  }, [paused, reduce])

  return (
    <div
      className="mt-3"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Abas */}
      <div className="flex gap-1.5" role="tablist" aria-label="Prévia do Premium">
        {TABS.map((t, idx) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={i === idx}
            onClick={() => setI(idx)}
            className={
              'inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors ' +
              (i === idx
                ? 'border-brand bg-brand text-white'
                : 'border-line bg-surface text-ink-mute hover:border-brand hover:text-brand')
            }
          >
            <Icon name={t.icon} size={12} />
            {t.label}
          </button>
        ))}
      </div>

      {/* "Tela" do app — prévia animada */}
      <div className="mt-2 overflow-hidden rounded border border-line bg-[linear-gradient(180deg,#F4F9F9_0%,#FFFFFF_100%)]">
        <div className="flex items-center justify-between border-b border-line-soft px-3 py-1.5">
          <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-brand">
            Prévia · {TABS[i].label}
          </span>
          <span className="flex items-center gap-1">
            {TABS.map((t, idx) => (
              <span
                key={t.key}
                className={
                  'h-1 rounded-full transition-all ' +
                  (i === idx ? 'w-4 bg-brand' : 'w-1.5 bg-line')
                }
                aria-hidden
              />
            ))}
          </span>
        </div>

        {/* Grid empilhado: todas as telas ocupam a MESMA célula (grid-area 1/1), então
            elas se sobrepõem no crossfade — sem flash vazio na troca — e o container
            se ajusta à tela mais alta sem altura fixa frágil. */}
        <div className="grid min-h-[190px] p-3.5">
          <AnimatePresence initial={false}>
            <motion.div
              key={active}
              className="[grid-area:1/1]"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {active === 'painel' && <PainelMock reduce={!!reduce} />}
              {active === 'orcamento' && <OrcamentoMock reduce={!!reduce} />}
              {active === 'comprovante' && <ComprovanteMock reduce={!!reduce} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── barra animada ─────────────────────────── */
function Bar({ pct, delay = 0, reduce, tone = 'brand' }: { pct: number; delay?: number; reduce: boolean; tone?: 'brand' | 'ghost' }) {
  return (
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line-soft">
      <motion.div
        className={tone === 'ghost' ? 'h-full rounded-full bg-brand/25' : 'h-full rounded-full bg-brand'}
        initial={reduce ? false : { width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: reduce ? 0 : 0.7, delay: reduce ? 0 : delay, ease: [0.2, 0.8, 0.2, 1] }}
      />
    </div>
  )
}

/* ─────────────────────────── Painel do mês ─────────────────────────── */
function PainelMock({ reduce }: { reduce: boolean }) {
  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10.5px] text-ink-mute">Faturamento no ano</p>
          <p className="font-mono text-[19px] font-bold leading-none text-ink">R$ 48.600</p>
        </div>
        <span className="rounded bg-brand-tint px-1.5 py-0.5 font-mono text-[10px] font-semibold text-brand">
          {PCT_TETO_EXEMPLO}% do teto
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Bar pct={PCT_TETO_EXEMPLO} reduce={reduce} />
        <span className="font-mono text-[10px] text-ink-faint">{tetoMeiCurto()}</span>
      </div>

      {/* mini gráfico mensal */}
      <div className="mt-3.5 flex items-end gap-1.5" aria-hidden>
        {[40, 55, 48, 70, 62, 85].map((h, idx) => (
          <motion.div
            key={idx}
            className="flex-1 rounded-sm bg-brand/70"
            initial={reduce ? false : { height: 2 }}
            animate={{ height: `${(h / 85) * 34 + 6}px` }}
            transition={{ duration: reduce ? 0 : 0.5, delay: reduce ? 0 : 0.15 + idx * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
          />
        ))}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded border border-line-soft bg-surface px-2.5 py-1.5">
          <p className="text-[10px] text-ink-faint">DAS de janeiro</p>
          <p className="flex items-center gap-1 text-[12.5px] font-semibold text-brand">
            <Icon name="check" size={12} /> Em dia
          </p>
        </div>
        <div className="rounded border border-line-soft bg-surface px-2.5 py-1.5">
          <p className="text-[10px] text-ink-faint">Sobrou no mês</p>
          <p className="font-mono text-[12.5px] font-semibold text-ink">R$ 3.100</p>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── Orçamento ─────────────────────────── */
function OrcamentoMock({ reduce }: { reduce: boolean }) {
  const rows = [
    { label: 'Receita', real: 90, prev: 100, valor: 'R$ 6.500' },
    { label: 'Custos', real: 53, prev: 57, valor: 'R$ 3.800' },
    { label: 'Sobrou', real: 62, prev: 60, valor: 'R$ 3.100' },
  ]
  return (
    <div>
      <p className="mb-2.5 text-[10.5px] text-ink-mute">
        Previsto <span className="text-ink-faint">×</span> real por categoria
      </p>
      <div className="space-y-3">
        {rows.map((r, idx) => (
          <div key={r.label}>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-[11.5px] font-semibold text-ink">{r.label}</span>
              <span className="font-mono text-[11.5px] font-semibold text-ink">{r.valor}</span>
            </div>
            <div className="relative flex items-center">
              <Bar pct={r.real} delay={idx * 0.12} reduce={reduce} />
              {/* marcador do previsto */}
              <span
                className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 rounded bg-ink/40"
                style={{ left: `calc(${r.prev}% - 1px)` }}
                aria-hidden
              />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 flex items-center gap-1 text-[10.5px] text-ink-faint">
        <span className="inline-block h-2 w-0.5 rounded bg-ink/40" /> previsto ·{' '}
        <span className="inline-block h-1.5 w-3 rounded-full bg-brand" /> real
      </p>
    </div>
  )
}

/* ─────────────────────────── Comprovante limpo ─────────────────────────── */
function ComprovanteMock({ reduce }: { reduce: boolean }) {
  const meses = [
    ['Jan/2026', 'R$ 5.420'],
    ['Fev/2026', 'R$ 6.180'],
    ['Mar/2026', 'R$ 5.890'],
  ]
  return (
    <div className="rounded border border-line bg-surface p-3">
      <div className="flex items-center justify-between border-b border-line-soft pb-2">
        <span className="flex items-center gap-1.5 text-[11.5px] font-bold text-ink">
          <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-brand text-white">
            <Icon name="file" size={10} />
          </span>
          Comprovante de Faturamento
        </span>
        <span className="rounded bg-brand-tint px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-brand">
          sem marca
        </span>
      </div>
      <div className="mt-2 space-y-1.5">
        {meses.map(([m, v], idx) => (
          <motion.div
            key={m}
            className="flex items-baseline justify-between"
            initial={reduce ? false : { opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduce ? 0 : 0.3, delay: reduce ? 0 : 0.1 + idx * 0.09 }}
          >
            <span className="text-[11.5px] text-ink-mute">{m}</span>
            <span className="font-mono text-[12px] font-semibold text-ink">{v}</span>
          </motion.div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-line-soft pt-2">
        <span className="flex items-center gap-1 text-[11px] font-semibold text-brand">
          <Icon name="check" size={12} /> Aceito por banco e imobiliária
        </span>
        <span className="font-mono text-[11.5px] font-bold text-ink">R$ 51.446</span>
      </div>
    </div>
  )
}
